import { ReactNode, useEffect, useState } from 'react'
import SharedData from '../common/SharedData'
import { IProductDraftModel } from './cors/_models'
import { getDraftProductsList } from './cors/_requests'
import Loader from '../common/Loader'
import Paginator from '../common/Paginator'
import DraftsTable from './draft/DraftsTable'
import DraftsTableFilter from './product-list/DraftsTableFilter'
import { prepareFilters } from '../orders/core/_requests'
import { useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { decodeHtmlEntities } from '../common/decodeHtml'

const PER_PAGE = 20

const DraftProductsList = () => {
  const { formatMessage } = useIntl()

  const [searchParams, setSearchParams] = useSearchParams()
  const { currentUser, language } = SharedData()
  const [productList, setProductList] = useState<IProductDraftModel[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [canFilter, setCanFilter] = useState(true)
  // The endpoint returns the full draft list, so the total is the list length.
  const totalResult = productList.length

  const handleDeleted = (requestId: number) => {
    const nextTotal = productList.filter((product) => product.request_id !== requestId).length
    const lastPage = Math.max(1, Math.ceil(nextTotal / PER_PAGE))
    const currentPage = +(searchParams.get('current_page') || 1)

    setProductList((products) =>
      products.filter((product) => product.request_id !== requestId)
    )

    // resultsData() already clamps the page it renders; this only keeps the
    // Paginator and the URL in sync when the last row on the last page was
    // deleted. The Product Name, Barcode and Model filters stay in the URL.
    if (currentPage > lastPage) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('current_page', lastPage.toString())
      setSearchParams(nextParams, {replace: true})
    }
  }

  useEffect(() => {
    getDate()
  }, [searchParams])
  const getDate = async () => {

    prepareFilters(searchParams, setSearchParams)
    setIsLoading(true)
    const queryParams = document.location.search

    try {
      const data = await getDraftProductsList(queryParams, currentUser?.token, language)

      if (data.data) {
        const productRequests = data.data.product_requests;

        setProductList(productRequests.map((item: any) => ({
          request_id: item.request_id,
          image: item.image,
          barcode: item.barcode,
          model: item.model,
          name: decodeHtmlEntities(item.name),
        })))
      }
    } catch (exp) {
      console.error(exp)
    }

    setIsLoading(false)
  }

  const resultsData = (): ReactNode => {
    // The draft-products endpoint returns the full list with no pagination
    // metadata, so page it on the client. Clamp the page so a stale/too-high
    // current_page (e.g. left over after filtering) never shows a blank table.
    const totalPages = Math.max(1, Math.ceil(totalResult / PER_PAGE))
    const currentPage = Math.min(Math.max(1, +(searchParams.get('current_page') || 1)), totalPages)
    const pagedList = productList.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)
    return (
        <div className='card-body py-3'>
          {isLoading && <Loader isFull={true} />}
          <DraftsTable products={pagedList} onDeleted={handleDeleted} />
          <Paginator totalResult={totalResult} perPage={PER_PAGE} />
        </div>
    )
  }

  return (
      <div className={`card`}>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>{formatMessage({ id: "draft_list" })}</span>
          </h3>
          <div className='card-toolbar'>
            <button
                type='button'
                className={`${
                    canFilter && 'active'
                } btn btn-sm  btn-color-primary btn-active-light-primary fs-6 gap-2`}
                onClick={() => setCanFilter(!canFilter)}
            >
              {formatMessage({ id: "filter" })} <i className='fas fa-filter'></i>
            </button>
          </div>
        </div>

        {canFilter && (
            <DraftsTableFilter />
        )}

        {resultsData()}
      </div>
  )
}

export default DraftProductsList
